import { API_ROUTES } from "../configuration/RoutesApi"
import { ERequestType } from "../Enum/ERequestType"
import { PostRequest } from "../services/ApiClientService"

interface PushSubscriptionKeysDto {
  p256dh: string
  auth: string
}

interface PushSubscriptionDto {
  endpoint: string
  expirationTime: number | null
  keys: PushSubscriptionKeysDto
}

interface PushPublicKeyDto {
  publicKey: string
}

interface ApiResponseDto<T> {
  success?: boolean
  data?: T
}

export interface IPushValidationResult {
  hasServiceWorkerSupport: boolean

  hasNotificationSupport: boolean

  notificationPermission: NotificationPermission | "unsupported"

  hasPushManagerSupport: boolean

  hasBrowserSubscription: boolean

  syncedWithServer: boolean
}

function toBase64Url(value: ArrayBuffer | null) {
  if (!value) {
    return ""
  }

  const bytes = new Uint8Array(value)
  let base64 = btoa(String.fromCharCode(...bytes))

  base64 = base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")

  return base64
}

function resolvePushRequestType() {
  if (localStorage.getItem(API_ROUTES.TOKEN_COURIER)) {
    return ERequestType.Courier
  }

  return ERequestType.Store
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)

  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")

  const rawData = atob(base64)

  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}

function mapSubscription(subscription: PushSubscription): PushSubscriptionDto {
  const json = subscription.toJSON()

  const p256dhFromKey = toBase64Url(subscription.getKey("p256dh"))

  const authFromKey = toBase64Url(subscription.getKey("auth"))

  return {
    endpoint: subscription.endpoint,
    expirationTime: subscription.expirationTime,
    keys: {
      p256dh: p256dhFromKey || json.keys?.p256dh || "",
      auth: authFromKey || json.keys?.auth || "",
    },
  }
}

async function sendSubscriptionToApi(payload: PushSubscriptionDto) {
  const response = await PostRequest<boolean | ApiResponseDto<boolean>>(
    API_ROUTES.PWA.SUBSCRIBE,
    payload,
    resolvePushRequestType(),
  )

  const normalizedSubscribeResponse = response as unknown as boolean | ApiResponseDto<boolean>

  if (typeof normalizedSubscribeResponse === "boolean") {
    return normalizedSubscribeResponse
  }

  return Boolean(normalizedSubscribeResponse.success && normalizedSubscribeResponse.data)
}

export async function subscribeToPush(registration: ServiceWorkerRegistration) {
  if (!("PushManager" in window)) {
    return false
  }

  const publicKeyResponse = await PostRequest<PushPublicKeyDto | ApiResponseDto<PushPublicKeyDto>>(
    API_ROUTES.PWA.GET_PUBLIC_KEY,
    {},
  )

  const normalizedPublicKeyResponse = publicKeyResponse as unknown as {
    publicKey?: string
    data?: PushPublicKeyDto | { publicKey?: string }
  }

  const publicKey = normalizedPublicKeyResponse.publicKey || normalizedPublicKeyResponse.data?.publicKey || ""

  if (!publicKey) {
    return false
  }

  const existingSubscription = await registration.pushManager.getSubscription()

  const subscription =
    existingSubscription ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }))

  const payload = mapSubscription(subscription)

  return sendSubscriptionToApi(payload)
}

export async function syncExistingPushSubscription(registration: ServiceWorkerRegistration) {
  if (!("PushManager" in window)) {
    return false
  }

  const existingSubscription = await registration.pushManager.getSubscription()

  if (!existingSubscription) {
    return false
  }

  const payload = mapSubscription(existingSubscription)

  return sendSubscriptionToApi(payload)
}

export async function forceResubscribeToPush(registration: ServiceWorkerRegistration) {
  if (!("PushManager" in window)) {
    return false
  }

  const existingSubscription = await registration.pushManager.getSubscription()

  if (existingSubscription) {
    await existingSubscription.unsubscribe().catch(() => false)
  }

  return subscribeToPush(registration)
}

export async function validatePushSubscription(
  registration: ServiceWorkerRegistration,
): Promise<IPushValidationResult> {
  const hasServiceWorkerSupport = "serviceWorker" in navigator

  const hasNotificationSupport = "Notification" in window

  const hasPushManagerSupport = "PushManager" in window

  const notificationPermission = hasNotificationSupport ? Notification.permission : "unsupported"

  if (!hasServiceWorkerSupport || !hasPushManagerSupport) {
    return {
      hasServiceWorkerSupport,
      hasNotificationSupport,
      notificationPermission,
      hasPushManagerSupport,
      hasBrowserSubscription: false,
      syncedWithServer: false,
    }
  }

  const existingSubscription = await registration.pushManager.getSubscription()

  if (!existingSubscription) {
    return {
      hasServiceWorkerSupport,
      hasNotificationSupport,
      notificationPermission,
      hasPushManagerSupport,
      hasBrowserSubscription: false,
      syncedWithServer: false,
    }
  }

  const payload = mapSubscription(existingSubscription)

  const syncedWithServer = await sendSubscriptionToApi(payload).catch(() => false)

  return {
    hasServiceWorkerSupport,
    hasNotificationSupport,
    notificationPermission,
    hasPushManagerSupport,
    hasBrowserSubscription: true,
    syncedWithServer,
  }
}
